(function () {
  'use strict';

  if (customElements.get('haro-feature-ticker')) return;

  class HaroFeatureTicker extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;

      this.track = this.querySelector('[data-haro-feature-track]');
      this.originalSet = this.querySelector('[data-haro-feature-set]');
      if (!this.track || !this.originalSet) return;

      const duplicateSet = this.originalSet.cloneNode(true);
      duplicateSet.removeAttribute('data-haro-feature-set');
      duplicateSet.setAttribute('aria-hidden', 'true');
      duplicateSet.setAttribute('data-nosnippet', '');
      duplicateSet.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      duplicateSet.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
        element.removeAttribute('data-shopify-editor-block');
      });
      this.track.appendChild(duplicateSet);

      this.dataset.ready = 'true';
      this.position = 0;
      this.speed = 48;
      this.hovered = false;
      this.dragging = false;
      this.pointerFocused = false;
      this.keyboardFocused = false;
      this.lastFrameTime = 0;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.updateMeasurements = this.updateMeasurements.bind(this);
      this.animate = this.animate.bind(this);
      this.handlePointerEnter = this.handlePointerEnter.bind(this);
      this.handlePointerLeave = this.handlePointerLeave.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleFocus = this.handleFocus.bind(this);
      this.handleBlur = this.handleBlur.bind(this);

      this.addEventListener('pointerenter', this.handlePointerEnter);
      this.addEventListener('pointerleave', this.handlePointerLeave);
      this.addEventListener('pointerdown', this.handlePointerDown);
      this.addEventListener('pointermove', this.handlePointerMove);
      this.addEventListener('pointerup', this.handlePointerUp);
      this.addEventListener('pointercancel', this.handlePointerUp);
      this.addEventListener('keydown', this.handleKeyDown);
      this.addEventListener('focus', this.handleFocus);
      this.addEventListener('blur', this.handleBlur);

      this.updateMeasurements();
      this.animationFrame = requestAnimationFrame(this.animate);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateMeasurements);
        this.resizeObserver.observe(this.originalSet);
      } else {
        window.addEventListener('resize', this.updateMeasurements, { passive: true });
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.updateMeasurements);
      this.removeEventListener('pointerenter', this.handlePointerEnter);
      this.removeEventListener('pointerleave', this.handlePointerLeave);
      this.removeEventListener('pointerdown', this.handlePointerDown);
      this.removeEventListener('pointermove', this.handlePointerMove);
      this.removeEventListener('pointerup', this.handlePointerUp);
      this.removeEventListener('pointercancel', this.handlePointerUp);
      this.removeEventListener('keydown', this.handleKeyDown);
      this.removeEventListener('focus', this.handleFocus);
      this.removeEventListener('blur', this.handleBlur);
      cancelAnimationFrame(this.animationFrame);
    }

    updateMeasurements() {
      if (!this.originalSet) return;
      this.setWidth = this.originalSet.getBoundingClientRect().width;
      this.speed = window.matchMedia('(max-width: 749px)').matches ? 42 : 48;
      this.position = this.normalize(this.position);
      this.render();
    }

    normalize(value) {
      if (!this.setWidth) return 0;
      return ((value % this.setWidth) + this.setWidth) % this.setWidth;
    }

    render() {
      this.track.style.transform = `translate3d(${-this.position}px, 0, 0)`;
    }

    animate(timestamp) {
      if (!this.lastFrameTime) this.lastFrameTime = timestamp;
      const elapsed = Math.min(64, timestamp - this.lastFrameTime);
      this.lastFrameTime = timestamp;

      if (!this.hovered && !this.dragging && !this.keyboardFocused && !this.motionQuery.matches) {
        this.position = this.normalize(this.position + (this.speed * elapsed) / 1000);
        this.render();
      }

      this.animationFrame = requestAnimationFrame(this.animate);
    }

    handlePointerEnter(event) {
      if (event.pointerType === 'mouse') this.hovered = true;
    }

    handlePointerLeave(event) {
      if (event.pointerType === 'mouse') this.hovered = false;
    }

    handlePointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      this.dragging = true;
      this.pointerFocused = true;
      this.dragStartX = event.clientX;
      this.dragStartPosition = this.position;
      this.dragPointerType = event.pointerType;
      this.classList.add('is-dragging');
      this.setPointerCapture?.(event.pointerId);
    }

    handlePointerMove(event) {
      if (!this.dragging) return;
      const delta = event.clientX - this.dragStartX;
      this.position = this.normalize(this.dragStartPosition - delta);
      this.render();
      if (Math.abs(delta) > 4 && event.cancelable) event.preventDefault();
    }

    handlePointerUp(event) {
      if (!this.dragging) return;
      this.dragging = false;
      this.pointerFocused = false;
      this.classList.remove('is-dragging');
      if (this.dragPointerType !== 'mouse') this.hovered = false;
      if (this.hasPointerCapture?.(event.pointerId)) this.releasePointerCapture(event.pointerId);
    }

    handleKeyDown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.keyboardFocused = true;
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      this.position = this.normalize(this.position + direction * 80);
      this.render();
    }

    handleFocus() {
      if (!this.pointerFocused) this.keyboardFocused = true;
    }

    handleBlur() {
      this.keyboardFocused = false;
      this.pointerFocused = false;
    }
  }

  customElements.define('haro-feature-ticker', HaroFeatureTicker);
})();
